import React, { createContext, useContext, useEffect, useState } from "react";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { useFetchApplicationConfiguration } from "@/service/ApplicationQueries";
import { useFetchOrganizationSettings } from "@/service/Settings/SettingsQueries";

// Enable plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Create a context with default values
const TimeContext = createContext({
	currentTime: dayjs().format(),
	timezone: dayjs.tz.guess(),
});

// Custom hook to use the TimeContext
export const useTime = () => useContext(TimeContext);

// TimeProvider component that wraps its children with the TimeContext provider
export const TimeProvider: React.FC = ({ children }) => {
	const { data: GeneralSettings } = useFetchOrganizationSettings();
	const { data: appConfig } = useFetchApplicationConfiguration();

	// State to store the current time and timezone
	const [currentTime, setCurrentTime] = useState(dayjs().format());
	const [timezone, setTimezone] = useState(dayjs.tz.guess());

	// Effect to update timezone based on settings and features
	useEffect(() => {
		if (appConfig?.Features.ServerUtcTime && GeneralSettings?.Timezone) {
			setTimezone(GeneralSettings?.Timezone); // Use timezone from settings if ServerUtcTime is true
		} else {
			setTimezone(dayjs.tz.guess()); // Default to local machine timezone
		}
	}, [appConfig, GeneralSettings]);

	// Effect to update current time every second
	useEffect(() => {
		const interval = setInterval(() => {
			if (appConfig?.Features.ServerUtcTime && GeneralSettings?.Timezone) {
				setCurrentTime(dayjs().tz(GeneralSettings?.Timezone).format()); // Use server timezone
			} else {
				setCurrentTime(dayjs().format()); // Use local machine time
			}
		}, 1000);

		return () => clearInterval(interval); // Cleanup interval on component unmount
	}, [appConfig, GeneralSettings]);

	return (
		<TimeContext.Provider value={{ currentTime, timezone }}>
			{children}
		</TimeContext.Provider>
	);
};
